"use client";

import React, { useEffect, useState } from "react";
import { Transition } from "@headlessui/react";
import Icon from "../Icon/Icon";
import styles from "./Toast.module.css";
import Button from "../Buttons/Button/Button";
import Typography from "../Typography/Typography";

const ContainerColor = {
    error: styles.error,
    warning: styles.warning,
    success: styles.success,
    info: styles.info,
}

interface Props {
    id: string;
    status: "error" | "warning" | "success" | "info";
    title: string;
    href?: string;
    removeToast?: (id: string) => void;
    defaultDelay?: number;
}

const DEFAULT_TOAST_DELAY = 5000;

const Toast = ({ id, status, title, href, removeToast, defaultDelay = DEFAULT_TOAST_DELAY }: Props) => {
    const [show, setShow] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShow(false); // we need to set show to false to trigger the transition then we remove it on afterLeave
        }, defaultDelay);

        return () => {
            clearTimeout(timer);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Removed defaultDelay from dependencies as this only runs on initial render.

    const handleCloseClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShow(false); // we need to set show to false to trigger the transition then we remove it on afterLeave
    };

    const handleAfterLeave = () => {
        if (removeToast) removeToast(id);
    };

    return (
        <Transition
            appear
            show={defaultDelay === 0 ? true : show}
            enter="transition-opacity duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={handleAfterLeave}
        >
            <div
                className={`${ContainerColor[status]} ${styles.toast}`}
                style={{ "--duration": `${defaultDelay}ms` } as React.CSSProperties}
            >
                <output className="flex gap-075">
                    <Icon icon="warning_fill" className={`${styles.statusIcon} flex-shrink-0`} />
                    <Typography as="h5" fontSize="080">{title}</Typography>
                </output>
                <div className="relative z-10">
                    <Button onClick={handleCloseClick} variant="none">
                        <Icon icon="cross" size="small" />
                    </Button>
                </div>
                <a href={href} title="Click to view notification" className="absolute inset-0 w-full h-full opacity-0 z-0">
                    Click to view notification
                </a>
            </div>
            {/* Your content goes here */}
        </Transition>
    );
};

export default Toast;
